import React, { useEffect, useState } from 'react'
import { HorizontalLoader } from 'Components'
import { CurrencyFormatter } from 'Helpers'
import { TimeConverter } from 'Helpers/TimeConverter'
import { boostCreditBXService } from 'BoostXpressAdmin/Services/boostCreditBXService'
import { BoostCreditUserDetails as BoostCreditUserDetailsType } from 'BoostXpressAdmin/Model/boostCreditXModel'
import './BoostCreditUserDetails.scss'

interface BoostCreditUserDetailsProps {
  userResourceId: string
}

const BoostCreditUserDetails: React.FC<BoostCreditUserDetailsProps> = ({ userResourceId }) => {
  const [boostCreditData, setBoostCreditData] = useState<BoostCreditUserDetailsType | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchBoostCreditDetails = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await boostCreditBXService.getUserDetails(userResourceId)
      setBoostCreditData(response.data.data)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load boost credit details')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (userResourceId) {
      fetchBoostCreditDetails()
    }
  }, [userResourceId])

  if (loading) {
    return (
      <section className='bx-details-section'>
        <h4 className='bx-details-section-title'>Boost Flex Facility</h4>
        <div className='bx-personal-info-box'>
          <div className='d-flex justify-content-center align-items-center' style={{ minHeight: '200px' }}>
            <HorizontalLoader />
          </div>
        </div>
      </section>
    )
  }

  if (error) {
    return (
      <section className='bx-details-section'>
        <h4 className='bx-details-section-title'>Boost Flex Facility</h4>
        <div className='bx-personal-info-box'>
          <div className='alert alert-danger' style={{ borderRadius: '8px' }}>{error}</div>
        </div>
      </section>
    )
  }

  if (!boostCreditData || !boostCreditData.facility) {
    return (
      <section className='bx-details-section'>
        <h4 className='bx-details-section-title'>Boost Flex Facility</h4>
        <div className='bx-personal-info-box'>
          <div className='alert alert-info' style={{ borderRadius: '8px' }}>No boost Flex data found</div>
        </div>
      </section>
    )
  }

  const { facility, overdue, drawdownTotals, eligibility, bvn, latestMandate } = boostCreditData

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return '#00c12b'
      case 'pending_activation':
        return '#ff9500'
      case 'suspended':
        return '#ff0000'
      case 'paused':
        return '#ff9500'
      case 'blacklisted':
        return '#000000'
      default:
        return '#808080'
    }
  }

  const getStatusBg = (status: string) => {
    switch (status) {
      case 'active':
        return '#d9f6df'
      case 'pending_activation':
        return '#fff3cd'
      case 'suspended':
        return '#ffcccc'
      case 'paused':
        return '#fff3cd'
      case 'blacklisted':
        return '#e0e0e0'
      default:
        return '#f5f5f5'
    }
  }

  return (
    <section className='bx-details-section'>
      <h4 className='bx-details-section-title'>Boost Flex Facility</h4>
      
      <div className='bx-metrics-grid'>
        <div className='bx-metric-card bx-metric-card--blue'>
          <div className='bx-metric-icon'>
            <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path d='M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 17C11.4477 17 11 16.5523 11 16C11 15.4477 11.4477 15 12 15C12.5523 15 13 15.4477 13 16C13 16.5523 12.5523 17 12 17ZM13 13H11V7H13V13Z' fill='#3378f3'/>
            </svg>
          </div>
          <div className='bx-metric-content'>
            <span className='bx-metric-label-text'>Status</span>
            <div 
              className='bx-status-badge'
              style={{ 
                backgroundColor: getStatusBg(facility.status),
                color: getStatusColor(facility.status)
              }}
            >
              {facility.status.replace(/_/g, ' ').toUpperCase()}
            </div>
          </div>
        </div>
        <div className='bx-metric-card bx-metric-card--green'>
          <div className='bx-metric-icon'>
            <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path d='M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM17 13H13V17H11V13H7V11H11V7H13V11H17V13Z' fill='#2ecc71'/>
            </svg>
          </div>
          <div className='bx-metric-content'>
            <span className='bx-metric-label-text'>Gross Limit</span>
            <h3 className='bx-metric-value-text'>₦{CurrencyFormatter(facility.grossLimitMajor)}</h3>
          </div>
        </div>
        <div className='bx-metric-card bx-metric-card--orange'>
          <div className='bx-metric-icon'>
            <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path d='M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM17 13H13V17H11V13H7V11H11V7H13V11H17V13Z' fill='#f0973d'/>
            </svg>
          </div>
          <div className='bx-metric-content'>
            <span className='bx-metric-label-text'>Available</span>
            <h3 className='bx-metric-value-text'>₦{CurrencyFormatter(facility.availableMajor)}</h3>
          </div>
        </div>
      </div>

      <div className='bx-metrics-grid' style={{ gridTemplateColumns: 'repeat(1, 1fr)' }}>
        <div className='bx-metric-card bx-metric-card--blue'>
          <div className='bx-metric-icon'>
            <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path d='M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM17 13H13V17H11V13H7V11H11V7H13V11H17V13Z' fill='#3378f3'/>
            </svg>
          </div>
          <div className='bx-metric-content'>
            <span className='bx-metric-label-text'>Outstanding</span>
            <h3 className='bx-metric-value-text'>₦{CurrencyFormatter(facility.outstandingMajor)}</h3>
          </div>
        </div>
      </div>

      <div className='bx-info-grid'>
        <div className='bx-info-item'>
          <span className='bx-info-label-text'>Mandate ID</span>
          <span className='bx-info-value-text'>{facility.mandateId || 'N/A'}</span>
        </div>
        <div className='bx-info-item'>
          <span className='bx-info-label-text'>Created At</span>
          <span className='bx-info-value-text'>{TimeConverter(facility.createdAt)}</span>
        </div>
        {facility.cancellationPenaltyUntil && (
          <div className='bx-info-item'>
            <span className='bx-info-label-text'>Penalty Until</span>
            <span className='bx-info-value-text'>{TimeConverter(facility.cancellationPenaltyUntil)}</span>
          </div>
        )}
        {facility.lastLimitReviewedAt && (
          <div className='bx-info-item'>
            <span className='bx-info-label-text'>Last Limit Review</span>
            <span className='bx-info-value-text'>{TimeConverter(facility.lastLimitReviewedAt)}</span>
          </div>
        )}
      </div>

      <div className='bx-personal-info-box'>
        <div className='bx-personal-info-content'>
          <h5 className='bx-details-section-title mb-3'>Drawdown Summary</h5>
          <div className='bx-info-grid'>
            <div className='bx-info-item'>
              <span className='bx-info-label-text'>Total Count</span>
              <span className='bx-info-value-text'>{drawdownTotals.totalCount}</span>
            </div>
            <div className='bx-info-item'>
              <span className='bx-info-label-text'>Unpaid Count</span>
              <span className='bx-info-value-text'>{drawdownTotals.unpaidCount}</span>
            </div>
            <div className='bx-info-item'>
              <span className='bx-info-label-text'>Total Borrowed</span>
              <span className='bx-info-value-text'>₦{CurrencyFormatter(drawdownTotals.totalBorrowedMajor)}</span>
            </div>
            <div className='bx-info-item'>
              <span className='bx-info-label-text'>Total Repaid</span>
              <span className='bx-info-value-text'>₦{CurrencyFormatter(drawdownTotals.totalRepaidMajor)}</span>
            </div>
          </div>

          {overdue.hasOverdueDebt && (
            <div className='alert alert-warning mt-3' style={{ borderRadius: '8px' }}>
              <strong>Overdue Debt:</strong> ₦{CurrencyFormatter(overdue.overdueMajor)}
              <br />
              <small>Overdue Drawdowns: {overdue.overdueDrawdownCount}</small>
              {overdue.oldestOverdueDueDate && (
                <>
                  <br />
                  <small>Oldest Due: {TimeConverter(overdue.oldestOverdueDueDate)}</small>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <div className='bx-info-grid' style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
        <div className='bx-personal-info-box'>
          <div className='bx-personal-info-content'>
            <h5 className='bx-details-section-title mb-3'>BVN Verification</h5>
            <div className='bx-info-grid' style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
              <div className='bx-info-item'>
                <span className='bx-info-label-text'>Verified</span>
                <span className='bx-info-value-text'>
                  {bvn.isVerified ? (
                    <span style={{ color: '#059669' }}>Yes</span>
                  ) : (
                    <span style={{ color: '#dc2626' }}>No</span>
                  )}
                </span>
              </div>
              {bvn.isVerified && (
                <>
                  <div className='bx-info-item'>
                    <span className='bx-info-label-text'>Verified At</span>
                    <span className='bx-info-value-text'>{TimeConverter(bvn.verifiedAt)}</span>
                  </div>
                  <div className='bx-info-item'>
                    <span className='bx-info-label-text'>Method</span>
                    <span className='bx-info-value-text'>{bvn.verificationMethod || 'N/A'}</span>
                  </div>
                </>
              )}
              <div className='bx-info-item'>
                <span className='bx-info-label-text'>Attempts</span>
                <span className='bx-info-value-text'>{bvn.attemptCount}</span>
              </div>
              {bvn.latestAttemptStatus && (
                <div className='bx-info-item'>
                  <span className='bx-info-label-text'>Latest Status</span>
                  <span className='bx-info-value-text'>{bvn.latestAttemptStatus}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className='bx-personal-info-box'>
          <div className='bx-personal-info-content'>
            <h5 className='bx-details-section-title mb-3'>Eligibility</h5>
            <div className='bx-info-grid' style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
              <div className='bx-info-item'>
                <span className='bx-info-label-text'>BVN Setup</span>
                <span className='bx-info-value-text'>
                  {eligibility.canProceedToBvnSetup ? (
                    <span style={{ color: '#059669' }}>Allowed</span>
                  ) : (
                    <span style={{ color: '#dc2626' }}>Not Allowed</span>
                  )}
                </span>
              </div>
              <div className='bx-info-item'>
                <span className='bx-info-label-text'>Mandate Setup</span>
                <span className='bx-info-value-text'>
                  {eligibility.canProceedToMandateSetup ? (
                    <span style={{ color: '#059669' }}>Allowed</span>
                  ) : (
                    <span style={{ color: '#dc2626' }}>Not Allowed</span>
                  )}
                </span>
              </div>
            </div>
            <div className='bx-criteria-list mt-3'>
              {eligibility.criteria.map((criterion: { key: string; label: string; met: boolean }, index: number) => (
                <div key={index} className='bx-criteria-item'>
                  <span className={`bx-criteria-icon ${criterion.met ? 'bx-met' : 'bx-not-met'}`}>
                    {criterion.met ? '✓' : '✗'}
                  </span>
                  <span className='bx-criteria-label'>{criterion.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {latestMandate && (
        <div className='bx-personal-info-box'>
          <div className='bx-personal-info-content'>
            <h5 className='bx-details-section-title mb-3'>Latest Collection Mandate</h5>
            <div className='bx-info-grid'>
              <div className='bx-info-item'>
                <span className='bx-info-label-text'>Mandate ID</span>
                <span className='bx-info-value-text'>{latestMandate.mandateId}</span>
              </div>
              <div className='bx-info-item'>
                <span className='bx-info-label-text'>Status</span>
                <span className='bx-info-value-text'>{latestMandate.status}</span>
              </div>
              <div className='bx-info-item'>
                <span className='bx-info-label-text'>Bank</span>
                <span className='bx-info-value-text'>{latestMandate.bankName}</span>
              </div>
              <div className='bx-info-item'>
                <span className='bx-info-label-text'>Account Number</span>
                <span className='bx-info-value-text'>{latestMandate.accountNumber}</span>
              </div>
              <div className='bx-info-item'>
                <span className='bx-info-label-text'>Account Name</span>
                <span className='bx-info-value-text'>{latestMandate.accountName}</span>
              </div>
              <div className='bx-info-item'>
                <span className='bx-info-label-text'>Amount</span>
                <span className='bx-info-value-text'>₦{CurrencyFormatter(latestMandate.amountMajor)}</span>
              </div>
              <div className='bx-info-item'>
                <span className='bx-info-label-text'>Debited Total</span>
                <span className='bx-info-value-text'>₦{CurrencyFormatter(latestMandate.debitedTotalMajor)}</span>
              </div>
              <div className='bx-info-item'>
                <span className='bx-info-label-text'>Remaining</span>
                <span className='bx-info-value-text'>₦{CurrencyFormatter(latestMandate.remainingMajor)}</span>
              </div>
              <div className='bx-info-item'>
                <span className='bx-info-label-text'>Start Date</span>
                <span className='bx-info-value-text'>{TimeConverter(latestMandate.startDate)}</span>
              </div>
              <div className='bx-info-item'>
                <span className='bx-info-label-text'>End Date</span>
                <span className='bx-info-value-text'>{TimeConverter(latestMandate.endDate)}</span>
              </div>
              <div className='bx-info-item'>
                <span className='bx-info-label-text'>Ready to Debit</span>
                <span className='bx-info-value-text'>
                  {latestMandate.readyToDebit ? (
                    <span style={{ color: '#059669' }}>Yes</span>
                  ) : (
                    <span style={{ color: '#dc2626' }}>No</span>
                  )}
                </span>
              </div>
              <div className='bx-info-item'>
                <span className='bx-info-label-text'>Expired</span>
                <span className='bx-info-value-text'>
                  {latestMandate.isExpired ? (
                    <span style={{ color: '#dc2626' }}>Yes</span>
                  ) : (
                    <span style={{ color: '#059669' }}>No</span>
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}

export default BoostCreditUserDetails
